import { useState } from 'react';
import type { JobDraft, Question, JobTest, QuestionType } from '@etnrlz-jobs/shared';
import { Spinner } from './ui';

export const emptyDraft: JobDraft = {
  title: '',
  summary: '',
  scope: '',
  requirements: '',
  niceToHave: '',
  location: '',
  workModel: 'remoto',
  employmentType: '',
  salaryRange: '',
  questions: [],
  tests: [],
  requireResume: true,
  requireVideo: true,
};

let nextId = 1;
const genId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${nextId++}`;

export function JobForm({
  initial,
  onSave,
  saving,
  saveLabel,
}: {
  initial: JobDraft;
  onSave: (draft: JobDraft) => void;
  saving: boolean;
  saveLabel: string;
}) {
  const [d, setD] = useState<JobDraft>(initial);
  const set = <K extends keyof JobDraft>(key: K, value: JobDraft[K]) =>
    setD((prev) => ({ ...prev, [key]: value }));

  const setQuestion = (i: number, patch: Partial<Question>) =>
    set(
      'questions',
      d.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)),
    );
  const setTest = (i: number, patch: Partial<JobTest>) =>
    set(
      'tests',
      d.tests.map((t, j) => (j === i ? { ...t, ...patch } : t)),
    );

  return (
    <div className="space-y-6">
      <section className="card space-y-4">
        <h2 className="font-semibold text-slate-100">Informações da vaga</h2>
        <div>
          <label className="label">Título</label>
          <input className="input" value={d.title} onChange={(e) => set('title', e.target.value)} />
        </div>
        <div>
          <label className="label">Resumo (aparece no topo da página pública)</label>
          <textarea
            className="input"
            rows={2}
            value={d.summary}
            onChange={(e) => set('summary', e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Modelo de trabalho</label>
            <select
              className="input"
              value={d.workModel}
              onChange={(e) => set('workModel', e.target.value as JobDraft['workModel'])}
            >
              <option value="remoto">Remoto</option>
              <option value="hibrido">Híbrido</option>
              <option value="presencial">Presencial</option>
            </select>
          </div>
          <div>
            <label className="label">Localização</label>
            <input
              className="input"
              placeholder="ex.: São Paulo/SP"
              value={d.location}
              onChange={(e) => set('location', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Tipo de contratação</label>
            <input
              className="input"
              placeholder="ex.: PJ, CLT"
              value={d.employmentType}
              onChange={(e) => set('employmentType', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Faixa salarial (opcional)</label>
            <input
              className="input"
              placeholder="ex.: R$ 8.000–12.000"
              value={d.salaryRange}
              onChange={(e) => set('salaryRange', e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="label">Escopo e responsabilidades (markdown)</label>
          <textarea
            className="input font-mono text-xs"
            rows={10}
            value={d.scope}
            onChange={(e) => set('scope', e.target.value)}
          />
        </div>
        <div>
          <label className="label">Requisitos (markdown)</label>
          <textarea
            className="input font-mono text-xs"
            rows={6}
            value={d.requirements}
            onChange={(e) => set('requirements', e.target.value)}
          />
        </div>
        <div>
          <label className="label">Diferenciais (markdown)</label>
          <textarea
            className="input font-mono text-xs"
            rows={4}
            value={d.niceToHave}
            onChange={(e) => set('niceToHave', e.target.value)}
          />
        </div>
      </section>

      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-100">Perguntas de triagem</h2>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() =>
              set('questions', [
                ...d.questions,
                { id: genId('q'), label: '', type: 'text', required: true },
              ])
            }
          >
            + Pergunta
          </button>
        </div>
        {d.questions.map((q, i) => (
          <div key={q.id} className="rounded-lg border border-ink-600 p-3">
            <div className="flex flex-wrap items-start gap-2">
              <input
                className="input flex-1"
                placeholder="Pergunta"
                value={q.label}
                onChange={(e) => setQuestion(i, { label: e.target.value })}
              />
              <select
                className="input w-36"
                value={q.type}
                onChange={(e) => setQuestion(i, { type: e.target.value as QuestionType })}
              >
                <option value="text">Texto</option>
                <option value="choice">Múltipla escolha</option>
                <option value="boolean">Sim/Não</option>
              </select>
              <label className="flex items-center gap-1.5 pt-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  checked={q.required}
                  onChange={(e) => setQuestion(i, { required: e.target.checked })}
                />
                Obrigatória
              </label>
              <button
                type="button"
                className="pt-2 text-xs text-red-400 hover:text-red-300"
                onClick={() =>
                  set(
                    'questions',
                    d.questions.filter((_, j) => j !== i),
                  )
                }
              >
                Remover
              </button>
            </div>
            {q.type === 'choice' && (
              <input
                className="input mt-2"
                placeholder="Opções separadas por | — ex.: Júnior|Pleno|Sênior"
                value={(q.options ?? []).join('|')}
                onChange={(e) =>
                  setQuestion(i, {
                    options: e.target.value.split('|').map((s) => s.trimStart()),
                  })
                }
              />
            )}
          </div>
        ))}
      </section>

      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-100">Testes práticos</h2>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() =>
              set('tests', [...d.tests, { id: genId('t'), title: '', instructions: '' }])
            }
          >
            + Teste
          </button>
        </div>
        {d.tests.map((t, i) => (
          <div key={t.id} className="space-y-2 rounded-lg border border-ink-600 p-3">
            <div className="flex items-center gap-2">
              <input
                className="input flex-1"
                placeholder="Título do teste"
                value={t.title}
                onChange={(e) => setTest(i, { title: e.target.value })}
              />
              <button
                type="button"
                className="text-xs text-red-400 hover:text-red-300"
                onClick={() =>
                  set(
                    'tests',
                    d.tests.filter((_, j) => j !== i),
                  )
                }
              >
                Remover
              </button>
            </div>
            <textarea
              className="input font-mono text-xs"
              rows={4}
              placeholder="Enunciado (markdown) — o candidato responde em texto"
              value={t.instructions}
              onChange={(e) => setTest(i, { instructions: e.target.value })}
            />
          </div>
        ))}
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold text-slate-100">Anexos do candidato</h2>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={d.requireResume}
            onChange={(e) => set('requireResume', e.target.checked)}
          />
          Currículo (PDF) obrigatório
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={d.requireVideo}
            onChange={(e) => set('requireVideo', e.target.checked)}
          />
          Vídeo de apresentação obrigatório
        </label>
      </section>

      <div className="flex justify-end">
        <button
          className="btn-primary"
          disabled={saving || !d.title.trim()}
          onClick={() => onSave(d)}
        >
          {saving && <Spinner />} {saveLabel}
        </button>
      </div>
    </div>
  );
}
